<template>
    <section :data-mui-theme="theme" class="slice slice-versus slice-versus--side" aria-label="versus" data-testid="slice-versus-side">
        <div class="slice-content slice-versus--side__wrapper">
            <div class="slice-versus--side__intro">
                <PrismicRichText
                    v-if="isFilled.richText(slice.primary.title)"
                    :field="slice.primary.title"
                    class="slice-versus--side__title mui-title"
                    data-testid="slice-versus-side-title"
                />
                <div class="slice-versus--side__cta">
                    <VJoyButton
                        v-if="isFilled.link(slice.primary.cta)"
                        :href="slice.primary.cta.url ? $withLocalePrefix(slice.primary.cta.url) : undefined"
                        variant="main"
                        size="large"
                        data-testid="slice-versus-side-cta"
                        :label="slice.primary.cta.text"
                    />
                </div>
            </div>

            <div class="slice-versus--side__comparison">
                <div class="slice-versus--side__first-card">
                    <div class="slice-versus--side__first-card-header">
                        <MuiTag v-if="isFilled.keyText(slice.primary.firstCardTag)">{{ slice.primary.firstCardTag }}</MuiTag>
                        <h3 v-if="isFilled.keyText(slice.primary.firstCardTitle)" class="mui-title">
                            {{ slice.primary.firstCardTitle }}
                        </h3>
                    </div>
                    <p
                        v-for="(firstCard, index) in slice.primary.firstCardParagraphs"
                        :key="`first-card-p-${index}`"
                        class="slice-versus--side__card-p slice-versus--side__first-card-p"
                        :data-testid="`slice-versus-side-first-card-p-${index}`"
                    >
                        {{ firstCard.paragraph }}
                    </p>
                </div>

                <div class="slice-versus--side__second-card mui-background-darker">
                    <div
                        :class="[
                            'slice-versus--side__second-card-header',
                            {'slice-versus--side__second-card-header--primary-tag': theme === 'primary'},
                        ]"
                    >
                        <MuiTag v-if="isFilled.keyText(slice.primary.secondCardTag)">{{ slice.primary.secondCardTag }}</MuiTag>
                        <h3 v-if="isFilled.keyText(slice.primary.secondCardTitle)" class="slice-versus--side__second-card-header-title mui-title">
                            {{ slice.primary.secondCardTitle }}
                        </h3>
                    </div>
                    <p
                        v-for="(secondCard, index) in slice.primary.secondCardParagraphs"
                        :key="`second-card-p-${index}`"
                        class="slice-versus--side__card-p slice-versus--side__second-card-p mui-background"
                        :data-testid="`slice-versus-side-second-card-p-${index}`"
                    >
                        {{ secondCard.paragraph }}
                    </p>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {isFilled} from '@prismicio/helpers';
    import {getSliceComponentProps, PrismicRichText} from '@prismicio/vue';
    import type {VersusSlice} from '@prismicio-types';
    import {MuiTag} from '#components';
    import {VJoyButton} from '@maltjoy/core-vue';

    type VersusSideInZone = Extract<VersusSlice, {variation: 'versusSide'}>;

    const props = defineProps(getSliceComponentProps<VersusSideInZone>(['slice', 'index', 'slices', 'context']));

    const theme = computed(() => props.slice.primary.theme);
    const rowCount = computed(() => props.slice.primary.firstCardParagraphs.length + 1);
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .slice-versus--side {
        &__wrapper {
            display: flex;
            flex-direction: column;
            gap: var(--joy-core-spacing-8);

            @include mq.screen_md(min) {
                gap: var(--joy-core-spacing-10);
            }

            @include mq.screen_lg(min) {
                flex-direction: row;
            }
        }

        &__intro {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: var(--joy-core-spacing-8);
            min-width: 30%;
            width: 50%;
        }

        &__cta {
            display: none;

            @include mq.screen_md(min) {
                display: block;
            }
        }

        &__title {
            text-align: left;
        }

        &__comparison {
            display: grid;
            gap: var(--joy-core-spacing-3);
            position: relative;

            @include mq.screen_md(min) {
                grid-template-columns: 1fr 1fr;
                grid-auto-flow: column;
                grid-template-rows: repeat(v-bind(rowCount), auto);
                column-gap: var(--joy-core-spacing-8);
                row-gap: var(--joy-core-spacing-3);

                &::after {
                    content: '';
                    position: absolute;
                    top: 0;
                    right: 0;
                    bottom: -32px;
                    left: calc(50% + var(--joy-core-spacing-4));
                    border-radius: var(--joy-core-radius-5);
                    background-color: var(--mui-color-background-darker);
                    z-index: 0;
                }
            }
        }

        &__first-card,
        &__second-card {
            display: grid;
            gap: var(--joy-core-spacing-2);
            padding: var(--joy-core-spacing-4);
            border-radius: var(--joy-core-spacing-7);
            text-align: center;

            @include mq.screen_md(min) {
                display: contents;
            }
        }

        &__first-card {
            background-color: var(--joy-color-neutral-10);
            border: 1px solid var(--joy-color-neutral-20);
        }

        &__first-card-header,
        &__second-card-header {
            z-index: 1;
            display: grid;
            grid-template-rows: auto 1fr;
            align-items: center;
            justify-items: center;
            padding-block-start: var(--joy-core-spacing-8);
            gap: var(--joy-core-spacing-4);
            padding: 0 var(--joy-core-spacing-2) var(--joy-core-spacing-4) var(--joy-core-spacing-2);

            @include mq.screen_md(min) {
                padding-top: var(--joy-core-spacing-8);
            }
        }

        &__first-card-header {
            .mui-c-tag {
                background-color: var(--joy-color-neutral-0);
                color: var(--joy-color-neutral-60);
                border: 1px solid var(--joy-color-neutral-50);
            }
        }

        &__second-card-header {
            &--primary-tag {
                .mui-c-tag {
                    background-color: var(--joy-color-neutral-0);
                    color: var(--joy-color-primary-50);
                    border: 1px solid var(--joy-color-primary-50);
                }
            }

            &-title {
                color: var(--joy-color-neutral-0);
            }
        }

        &__first-card-p {
            background-color: var(--joy-color-neutral-20);
        }

        &__second-card-p {
            @include mq.screen_md(min) {
                margin: 0 var(--joy-core-spacing-8);
            }
        }

        &__card-p {
            display: flex;
            justify-content: center;
            align-items: center;
            line-height: var(--joy-line-height-large);
            color: var(--joy-color-neutral-60);
            letter-spacing: var(--joy-font-letter-spacing-tight);
            padding: var(--joy-core-spacing-5) var(--joy-core-spacing-4);
            border-radius: var(--joy-core-radius-4);

            @include mq.screen_md(min) {
                z-index: 1;
            }
        }
    }
</style>
